import type { Transaction } from "@rocicorp/zero";
import type { ZeroContext } from "../types";
import { zql } from "../zql";

/**
 * The tenancy checks every mutator runs before it writes. Mutations execute on a pool that
 * bypasses row level security, so these are the whole wall, and the rule they enforce is that no
 * id a client supplies reaches a row without being compared against the caller's organisation
 * first — a foreign key is checked with row level security off, so an unchecked id is a row
 * written.
 *
 * One id a client supplies is deliberately not guarded, and it is named here so the claim above
 * stays true: the ticket ids `plans.batchSkipTickets` and `plans.toggleSkippedTicket` push into
 * `plans.skipped_tickets`. That column is a bare `uuid[]` on the caller's own plan row with no
 * foreign key and no reader that resolves it — the board uses it to hide tickets it already
 * holds — so a foreign id there names nothing, delivers nothing and hides nothing.
 */
export namespace Guards {
	/**
	 * Resolve a plan and verify it belongs to the caller's organisation. Every mutation on the
	 * sync path funnels through this or `assertCrewInOrg` — assignments and crews carry no
	 * usable tenancy of their own (a partner organisation's crew holds a foreign orgId by
	 * design), so the plan is the single tenancy root the guards check against.
	 *
	 * @param tx - The Zero transaction the mutator runs in.
	 * @param ctx - The server-derived caller context.
	 * @param planId - The plan the mutation ultimately targets.
	 * @returns The plan row, so callers can reuse the read.
	 */
	export async function assertPlanInOrg(tx: Transaction, ctx: ZeroContext, planId: string) {
		const plan = await tx.run(zql.plans.where("id", "=", planId).one());
		if (!plan || plan.orgId !== ctx.orgId) {
			throw new Error("Plan is outside the caller's organisation");
		}
		return plan;
	}

	/**
	 * Resolve a plan, verify it belongs to the caller's organisation, and refuse when that day is
	 * published. A published day is handed out — the people on it were notified of what it says —
	 * so arranging it further belongs behind a pull-back, the same lock the REST writes carry
	 * (`Plans.assignVehicleCrew`, `patchPlanSkipTicket`, `postPlanVehicleAssignmentUser`).
	 *
	 * The mutators that hold the lock: the three ticket-arranging ones through
	 * `assertCrewOnEditableDay`, `vehicleAssignments.insert` and `.delete`, every
	 * `vehicleAssignmentPlans` mutator, and `plans.batchSkipTickets` and `.toggleSkippedTicket`.
	 * `plans.toggleNotificationsEnabled` does not: whether the day's notifications go out is a
	 * setting of the day, not an arrangement of it. Both sides of the wire run the guard, so a
	 * board that is published in the replica refuses optimistically, and a stale client's apply
	 * reverts on the server rebase, where the row is read as it stands.
	 *
	 * @param tx - The Zero transaction the mutator runs in.
	 * @param ctx - The caller context of whichever side is running.
	 * @param planId - The plan the mutation targets.
	 * @returns The plan row, so callers can reuse the read.
	 */
	export async function assertPlanOnEditableDay(tx: Transaction, ctx: ZeroContext, planId: string) {
		const plan = await assertPlanInOrg(tx, ctx, planId);
		if (plan.state === "published") {
			throw new Error("A published day refuses further arrangement");
		}
		return plan;
	}

	/**
	 * Resolve a crew and verify its plan belongs to the caller's organisation. The crew's own
	 * orgId is deliberately not compared — a partner organisation's crew on the caller's plan
	 * is the external-plan feature, and the host manages it like any of its own crews.
	 *
	 * @param tx - The Zero transaction the mutator runs in.
	 * @param ctx - The server-derived caller context.
	 * @param crewId - The crew the mutation targets.
	 * @returns The crew row, so callers can reuse the read.
	 */
	export async function assertCrewInOrg(tx: Transaction, ctx: ZeroContext, crewId: string) {
		const crew = await tx.run(zql.crews.where("id", "=", crewId).one());
		if (!crew) throw new Error("Crew not found");
		await assertPlanInOrg(tx, ctx, crew.planId);
		return crew;
	}

	/**
	 * Resolve a crew, verify its plan belongs to the caller's organisation, and refuse when that
	 * day is published — `assertPlanOnEditableDay` for the crew's plan, which carries the lock and
	 * the list of mutators holding it.
	 *
	 * The three ticket-arranging mutators and `vehicleAssignments.insert` run this instead of
	 * `assertCrewInOrg`.
	 *
	 * @param tx - The Zero transaction the mutator runs in.
	 * @param ctx - The caller context of whichever side is running.
	 * @param crewId - The crew the mutation targets.
	 * @returns The crew row, so callers can reuse the read.
	 */
	export async function assertCrewOnEditableDay(tx: Transaction, ctx: ZeroContext, crewId: string) {
		const crew = await tx.run(zql.crews.where("id", "=", crewId).one());
		if (!crew) throw new Error("Crew not found");
		await assertPlanOnEditableDay(tx, ctx, crew.planId);
		return crew;
	}

	/**
	 * Resolve a crew and verify the caller's organisation *owns* it — that the people going into
	 * the slot are the caller's people, whoever's board the slot stands on.
	 *
	 * This is the opposite test from `assertCrewInOrg` and the two are not interchangeable.
	 * Staffing belongs to the organisation that owns the crew and to nobody else, the host whose
	 * plan it sits on included: a legal requirement rather than a product preference. It is
	 * therefore the one guard where a foreign plan is acceptable and a foreign crew is not.
	 *
	 * A crew whose organisation is NULL — the state `crews.org_id` reaches through
	 * ON DELETE SET NULL when an organisation is deleted — is owned by nobody and staffable by
	 * nobody. Rejecting it here keeps the mutator from attempting a write the matching database
	 * policy would refuse anyway.
	 *
	 * @param tx - The Zero transaction the mutator runs in.
	 * @param ctx - The server-derived caller context.
	 * @param crewId - The crew being staffed.
	 * @returns The crew row, so callers can reuse the read and stamp `orgId` from it.
	 */
	export async function assertCrewOwnedByCaller(tx: Transaction, ctx: ZeroContext, crewId: string) {
		const crew = await tx.run(zql.crews.where("id", "=", crewId).one());
		if (!crew) throw new Error("Crew not found");
		if (!crew.orgId || crew.orgId !== ctx.orgId) {
			throw new Error("Only the organisation that owns a crew may staff it");
		}
		return crew;
	}

	/**
	 * Resolve a project and verify it belongs to the caller's organisation. Ticket assignments
	 * put tickets of the host's projects onto crews, so an insert must not accept a foreign
	 * project id even when the crew checks out.
	 *
	 * @param tx - The Zero transaction the mutator runs in.
	 * @param ctx - The server-derived caller context.
	 * @param projectId - The project the mutation references.
	 * @returns The project row, so callers can reuse the read.
	 */
	export async function assertProjectInOrg(tx: Transaction, ctx: ZeroContext, projectId: string) {
		const project = await tx.run(zql.projects.where("id", "=", projectId).one());
		if (!project || project.orgId !== ctx.orgId) {
			throw new Error("Project is outside the caller's organisation");
		}
		return project;
	}

	/**
	 * Resolve a ticket and verify it belongs to the caller's organisation.
	 *
	 * A ticket id travels from the client into a junction row, and a foreign key is checked with
	 * row level security switched off, so the insert would otherwise succeed and put a foreign
	 * ticket on the caller's board. The row it creates is not merely stray: the queries that read
	 * a crew's work carry `related("ticket")`, so the caller would then read the ticket's own text
	 * back through a junction row they wrote themselves.
	 *
	 * @param tx - The Zero transaction the mutator runs in.
	 * @param ctx - The server-derived caller context.
	 * @param ticketId - The ticket the mutation references.
	 * @returns The ticket row, so callers can reuse the read.
	 */
	export async function assertTicketInOrg(tx: Transaction, ctx: ZeroContext, ticketId: string) {
		const ticket = await tx.run(zql.tickets.where("id", "=", ticketId).one());
		if (!ticket || ticket.orgId !== ctx.orgId) {
			throw new Error("Ticket is outside the caller's organisation");
		}
		return ticket;
	}

	/**
	 * Refuse the mutation unless the caller may arrange the project's work on a board: holders
	 * of `update Plan` arrange every project's, and the project's members, their active
	 * substitutes and admins arrange their own — `ctx.arrangeableTicketProjects()`, produced
	 * from the caller's claims on both sides of the wire. This is not a tenancy check and does
	 * not replace one: the `*InOrg`/editable-day guards still run first.
	 *
	 * `undefined` is the client before its rules resolve; the optimistic run passes and the
	 * server's run — whose context always answers — decides. The server builds the value per
	 * request in `getSyncCaller`, so a missing answer cannot happen where it counts.
	 *
	 * @param ctx - The caller context of whichever side is running.
	 * @param projectId - The project of the ticket being arranged.
	 */
	export function assertProjectWorkArrangeable(ctx: ZeroContext, projectId: string) {
		const reach = ctx.arrangeableTicketProjects();
		if (reach === undefined) return;
		if (reach === "all" || reach.includes(projectId)) return;
		throw new Error("Arranging this project's work needs the board right or the project");
	}

	/**
	 * Verify the person a mutation names belongs to the caller's organisation.
	 *
	 * Two writes accept a bare user id from the client — staffing a crew and putting a driver
	 * behind a wheel — and both feed that column straight to `Plans.getInvolvedUserIds`, which is
	 * what a published day notifies. Without this an organisation could name anybody at all and
	 * the publish would mail them.
	 *
	 * This is the one guard that reads SQL rather than ZQL, and it has to: `organization_members`
	 * is not a synced table, and it cannot become one usefully — no query delivers it, so a
	 * client's replica would hold none of its rows and the optimistic run would refuse every
	 * staffing. The check therefore runs on the server's own transaction, where the membership row
	 * is one statement away, and the client run skips it. An optimistic apply the server then
	 * refuses is the ordinary shape of a rejection in Zero: the rebase undoes it.
	 *
	 * @param tx - The Zero transaction the mutator runs in.
	 * @param ctx - The server-derived caller context.
	 * @param userId - The person the mutation names.
	 */
	export async function assertUserInOrg(tx: Transaction, ctx: ZeroContext, userId: string) {
		if (tx.location !== "server") return;
		const rows = await tx.dbTransaction.query(
			"SELECT 1 FROM organization_members WHERE user_id = $1 AND org_id = $2 LIMIT 1",
			[userId, ctx.orgId],
		);
		if ([...rows].length === 0) {
			throw new Error("User is not a member of the caller's organisation");
		}
	}

	/**
	 * Verify the vehicle a mutation names belongs to the caller's organisation.
	 *
	 * Two writes accept a bare vehicle id from the client — a truck onto a crew and a truck onto
	 * the day — and neither row carries anything else that ties the vehicle to a tenancy. The
	 * foreign key to `vehicles` is checked with row level security switched off, so a foreign id
	 * is accepted and the assignment lands stamped with the caller's own organisation: a row its
	 * owner cannot see, on a board that then reads the licence plate back through
	 * `related("vehicle")`.
	 *
	 * Read as ZQL and on the server's transaction only, for the same reason `assertUserInOrg`
	 * runs there: no registered query delivers a bare `vehicles` row, so a client's replica holds
	 * only the trucks already standing on a board, while the picker a planner drags from is the
	 * REST list (`useVehicles`). A check in the optimistic run would therefore refuse every
	 * assignment of a truck that is not already on the day. The server run is the authoritative
	 * one; an optimistic apply it refuses is undone by the rebase.
	 *
	 * @param tx - The Zero transaction the mutator runs in.
	 * @param ctx - The server-derived caller context.
	 * @param vehicleId - The vehicle the mutation names.
	 */
	export async function assertVehicleInOrg(tx: Transaction, ctx: ZeroContext, vehicleId: string) {
		if (tx.location !== "server") return;
		const vehicle = await tx.run(zql.vehicles.where("id", "=", vehicleId).one());
		if (!vehicle || vehicle.orgId !== ctx.orgId) {
			throw new Error("Vehicle is outside the caller's organisation");
		}
	}
}
